import type {
  AddGuestRequest,
  AdminCompletePatrolRequest,
  Patrol,
} from '@local/common-lib';
import {
  addGuestRequestContract,
  adminCompletePatrolRequestContract,
} from '@local/common-lib';
import { useMediaQuery, useTheme } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Issue } from 'validata';
import { useLocalStorage } from '../../hooks/use-local-storage.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import type { ExistingEntityViewProps } from '../../utils/types.js';
import { formikCheck } from '../../utils/validation.js';
import type { AdminCompletePatrolData } from './admin-complete.js';
import type { AddPatrollerData } from './display.view.js';
import { DisplayPatrolView } from './display.view.js';
import { ExistingPatrolPage } from './existing-patrol.js';

export const DisplayPatrolContent: FC<ExistingEntityViewProps<Patrol.Type>> = ({
  data: patrol,
  id,
  refresh,
  fetchNow,
}) => {
  const auth = useAuthenticated();
  const navigate = useNavigate();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [showTimeTable, setShowTimeTable] = useLocalStorage(
    'timeline-table',
    !smallScreen,
  );
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [showAdminComplete, setShowAdminComplete] = useState(false);
  const [showAddPatroller, setShowAddPatroller] = useState(false);
  const [startEccWarningOpen, setStartEccWarningOpen] = useState(false);
  const [busy, showBusy] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const patrollers = patrol.patrollers ?? {};
  const myAssignment = patrollers[auth.userInfo.userId];
  const isMine =
    myAssignment && !myAssignment.declined && !myAssignment.removed;
  const myAssignmentPending =
    !!myAssignment &&
    !myAssignment.accepted &&
    !myAssignment.declined &&
    !myAssignment.removed;

  const addGuestForm = useFormik<AddGuestRequest>({
    initialValues: { email: '', name: '', phone: '' },
    onReset: () => setShowAddGuest(false),
    onSubmit: (values) => {
      if (!values.name) {
        enqueueSnackbar('Please enter guest name', { variant: 'warning' });
        return;
      }
      auth.axios
        .post(`/api/v1/patrol/${encodeURIComponent(id)}/guest`, {
          email: values.email || undefined,
          name: values.name,
          phone: values.phone || undefined,
        })
        .then(() => {
          refresh();
          return undefined;
        })
        .catch((err: Error) => {
          Sentry.captureException(err);
          enqueueSnackbar(`Unable to update: ${err.message}`, {
            variant: 'error',
          });
        });
    },
    validate: (value) => formikCheck(addGuestRequestContract, value),
    validateOnBlur: true,
  });

  const handleAcceptPatroller = (patrollerId: string): void => {
    auth.axios
      .post(
        `/api/v1/patrol/${encodeURIComponent(id)}/patroller/${encodeURIComponent(patrollerId)}/accept`,
      )
      .then(() => {
        refresh();
        return undefined;
      })
      .catch((err: Error) => {
        Sentry.captureException(err);
        enqueueSnackbar(`Unable to update: ${err.message}`, {
          variant: 'error',
        });
      });
  };

  const handleDeclinePatroller = (patrollerId: string): void => {
    auth.axios
      .post(
        `/api/v1/patrol/${encodeURIComponent(id)}/patroller/${encodeURIComponent(patrollerId)}/decline`,
      )
      .then(() => {
        refresh();
        return undefined;
      })
      .catch((err: Error) => {
        Sentry.captureException(err);
        enqueueSnackbar(`Unable to update: ${err.message}`, {
          variant: 'error',
        });
      });
  };

  const handleMyAssignment = (action: 'accept' | 'decline'): void => {
    showBusy(true);
    auth.axios
      .post(`/api/v1/patrol/${encodeURIComponent(id)}/my-assignment/${action}`)
      .then(() => {
        const actionPastTense = action === 'accept' ? 'accepted' : 'declined';
        enqueueSnackbar(`Assignment ${actionPastTense} successfully.`, {
          variant: 'success',
        });
        refresh();
        return undefined;
      })
      .catch((err: Error) => {
        Sentry.captureException(err);
        enqueueSnackbar(`Unable to ${action} assignment: ${err.message}`, {
          variant: 'error',
        });
      })
      .finally(() => showBusy(false));
  };

  const handleRemoveGuest = (guestId: string): void => {
    auth.axios
      .delete(
        `/api/v1/patrol/${encodeURIComponent(id)}/guest/${encodeURIComponent(guestId)}`,
      )
      .then(() => {
        refresh();
        return undefined;
      })
      .catch((err: Error) => {
        Sentry.captureException(err);
        enqueueSnackbar(`Unable to update: ${err.message}`, {
          variant: 'error',
        });
      });
  };

  const handleRemovePatroller = (patrollerId: string): void => {
    auth.axios
      .delete(
        `/api/v1/patrol/${encodeURIComponent(id)}/patroller/${encodeURIComponent(patrollerId)}`,
      )
      .then(() => {
        refresh();
        return undefined;
      })
      .catch((err: Error) => {
        Sentry.captureException(err);
        enqueueSnackbar(`Unable to update: ${err.message}`, {
          variant: 'error',
        });
      });
  };

  const handleSetPrimaryPatroller = (patrollerId: string): void => {
    auth.axios
      .post(
        `/api/v1/patrol/${encodeURIComponent(id)}/patroller/${encodeURIComponent(patrollerId)}/set-primary`,
      )
      .then(() => {
        refresh();
        return undefined;
      })
      .catch((err: Error) => {
        Sentry.captureException(err);
        enqueueSnackbar(`Unable to update: ${err.message}`, {
          variant: 'error',
        });
      });
  };

  const handleCancel = (didNotShow: boolean): void => {
    showBusy(true);
    auth.axios
      .post(`/api/v1/patrol/${encodeURIComponent(id)}/cancel`, {
        didNotShow: patrol.when < new Date() ? didNotShow : undefined,
      })
      .then(() => {
        showBusy(false);
        navigate('/patrols/scheduled', { replace: true });
        return undefined;
      })
      .catch((err: Error) => {
        Sentry.captureException(err);
        enqueueSnackbar(`Unable to cancel: ${err.message}`, {
          variant: 'error',
        });
        showBusy(false);
      });
  };

  const handleFinalize = (): void => {
    showBusy(true);
    auth.axios
      .post(`/api/v1/patrol/${encodeURIComponent(id)}/finalize`)
      .then(() => {
        refresh();
        showBusy(false);
        return undefined;
      })
      .catch((err: Error) => {
        Sentry.captureException(err);
        enqueueSnackbar(`Unable to update: ${err.message}`, {
          variant: 'error',
        });
        showBusy(false);
      });
  };

  const patrolStart = DateTime.fromJSDate(patrol.when);
  const adminCompleteForm = useFormik<AdminCompletePatrolData>({
    enableReinitialize: true,
    initialValues: {
      finished: patrolStart.plus({ hours: patrol.hours }).toJSDate(),
      guestsPresent: Object.entries(patrol.guests ?? {})
        .filter(([, guest]) => !guest.removed)
        .map(([guestId]) => guestId),
      notes: '',
      patrollersPresent: Object.keys(patrollers).filter(
        (personId) =>
          !patrollers[personId].declined && !patrollers[personId].removed,
      ),
      started: patrol.started?.started ?? patrolStart.toJSDate(),
    },
    onReset: () => setShowAdminComplete(false),
    onSubmit: async (values) => {
      const body: AdminCompletePatrolRequest = {
        finished: values.finished,
        guestsPresent: values.guestsPresent,
        notes: values.notes || undefined,
        patrollersPresent: values.patrollersPresent,
        started: values.started,
      };
      try {
        await auth.axios.post(
          `/api/v1/patrol/${encodeURIComponent(id)}/admin-complete`,
          body,
        );
        enqueueSnackbar('Patrol marked as completed', { variant: 'success' });
        setShowAdminComplete(false);
        refresh();
      } catch (err: unknown) {
        const error = err as Error & {
          response?: { data?: { issues?: Issue[] }; status?: number };
        };
        if (error.response?.status === 400 && error.response.data?.issues) {
          enqueueSnackbar(
            `Unable to complete patrol: ${error.response.data.issues
              .map((issue) => issue.reason)
              .join(', ')}`,
            { variant: 'error' },
          );
          return;
        }
        Sentry.captureException(err);
        enqueueSnackbar(`Unable to complete patrol: ${error.message}`, {
          variant: 'error',
        });
      }
    },
    validate: (value) => formikCheck(adminCompletePatrolRequestContract, value),
  });

  const continueToStart = useCallback(
    (): void => navigate(`/patrol/${encodeURIComponent(id)}/record`),
    [navigate, id],
  );

  const handleStartEccWarningClose = (): void => {
    setStartEccWarningOpen(false);
    refresh();
  };

  const onStart = useCallback((): void => {
    showBusy(true);
    fetchNow()
      .then((latestData) => {
        showBusy(false);
        Sentry.addBreadcrumb({
          data: {
            eccLogOnNotification: latestData.eccLogOnNotification,
            eccLogOnResponse: latestData.eccLogOnResponse,
            id,
          },
          level: 'info',
          message: 'Start clicked',
        });

        if (
          latestData.type !== 'event' &&
          (!latestData.eccLogOnNotification || !latestData.eccLogOnResponse)
        ) {
          Sentry.captureEvent({
            extra: {
              latestData,
              patrolId: id,
              userId: auth.userInfo.userId,
            },
            level: 'warning',
            message: 'Starting patrol without ECC log-on',
          });
          setStartEccWarningOpen(true);
        } else {
          Sentry.captureEvent({
            extra: {
              latestData,
              patrolId: id,
              userId: auth.userInfo.userId,
            },
            level: 'debug',
            message: 'Starting patrol',
          });
          continueToStart();
        }
        return null;
      })
      .catch((err: Error) => {
        Sentry.captureException(err);
        enqueueSnackbar(`Unable to update: ${err.message}`, {
          variant: 'error',
        });
      });
  }, [id, continueToStart, enqueueSnackbar, fetchNow, auth.userInfo.userId]);

  const addPatrollerForm = useFormik<AddPatrollerData>({
    initialValues: { patroller: null },
    // TODO validate,
    onReset: () => setShowAddPatroller(false),
    onSubmit: (values) => {
      if (!values.patroller) {
        enqueueSnackbar('Please select a patroller', { variant: 'warning' });
        return;
      }
      auth.axios
        .post(
          `/api/v1/patrol/${encodeURIComponent(id)}/patroller/${encodeURIComponent(values.patroller?._id)}`,
        )
        .then(() => {
          refresh();
          return undefined;
        })
        .catch((err: Error) => {
          Sentry.captureException(err);
          enqueueSnackbar(`Unable to update: ${err.message}`, {
            variant: 'error',
          });
        });
    },
  });

  return (
    <DisplayPatrolView
      addGuestForm={addGuestForm}
      addPatrollerForm={addPatrollerForm}
      adminCompleteForm={adminCompleteForm}
      busy={busy}
      data={patrol}
      handleAcceptPatroller={handleAcceptPatroller}
      handleDeclinePatroller={handleDeclinePatroller}
      handleMyAssignmentAccept={() => handleMyAssignment('accept')}
      handleMyAssignmentDecline={() => handleMyAssignment('decline')}
      handleRemoveGuest={handleRemoveGuest}
      handleRemovePatroller={handleRemovePatroller}
      handleSetPrimaryPatroller={handleSetPrimaryPatroller}
      handleStartEccWarningClose={handleStartEccWarningClose}
      handleStartEccWarningContinue={continueToStart}
      id={id}
      isAdmin={auth.userInfo.hasRole('admin')}
      isMine={isMine}
      myAssignmentPending={myAssignmentPending}
      onCancel={handleCancel}
      onEccLogOn={() =>
        navigate(`/patrol/${encodeURIComponent(id)}/ecc-log-on`)
      }
      onEdit={() => navigate(`/patrol/${encodeURIComponent(id)}/edit`)}
      onFinalize={handleFinalize}
      onRecord={() => navigate(`/patrol/${encodeURIComponent(id)}/record`)}
      onShowAddGuest={() => {
        setShowAddGuest(true);
        setShowAddPatroller(false);
      }}
      onShowAddPatroller={() => {
        setShowAddGuest(false);
        setShowAddPatroller(true);
      }}
      onShowAdminComplete={() => setShowAdminComplete(true)}
      onStart={onStart}
      setShowTimeTable={setShowTimeTable}
      showAddGuest={showAddGuest}
      showAddPatroller={showAddPatroller}
      showAdminComplete={showAdminComplete}
      showTimeTable={showTimeTable}
      startEccWarningOpen={startEccWarningOpen}
    />
  );
};

export const DisplayPatrolPage: FC = () => (
  <ExistingPatrolPage view={DisplayPatrolContent} />
);
